import { kAgentV2SourceUnavailableLabel, kAgentV2UnverifiedSourceLabel, labelAgentV2Citations } from '@/agent-v2/references/agent-v2-citation-labels'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { kAgentLegalCitationLinkHrefRegex, kCitationMarkerRegex } from '@/util/agent-citations'
import { kArtifactReferenceMarkerIdAttr } from './artifact-citation-helpers'

export enum AgentV2ArtifactCitationTargetKind {
  VIEWER = 'viewer',
  WEB = 'web',
}

export type AgentV2ArtifactCitationViewerTarget = {
  kind: AgentV2ArtifactCitationTargetKind
  displayName: string
  viewerUrl: string
}

type ReplaceAgentV2ReadOnlyArtifactCitationsOptions = {
  html: string
  markdown: string
  referencesByCitationId: Record<string, AgentV2Reference>
  viewerTargetsByCitationId: Partial<Record<string, AgentV2ArtifactCitationViewerTarget>>
}

/**
 * Replace citation tokens in already-sanitized read-only artifact HTML.
 *
 * Labels come from the raw Markdown so grouped and repeated citations match the
 * editable canvas. Targets come from stored references and are validated here;
 * this post-pass never preserves a user-authored citation link.
 */
export function replaceAgentV2ReadOnlyArtifactCitations({
  html,
  markdown,
  referencesByCitationId,
  viewerTargetsByCitationId,
}: ReplaceAgentV2ReadOnlyArtifactCitationsOptions): string {
  if (typeof document === 'undefined') return html

  const labeledCitations = labelAgentV2Citations(markdown, referencesByCitationId)
  let nextCitationIndex = 0
  const template = document.createElement('template')
  template.innerHTML = html

  function nextCitation(citationId: string, isLegalCitation: boolean) {
    const citation = labeledCitations[nextCitationIndex]
    const isExpectedForm = citation ? (citation.authorityText !== null) === isLegalCitation : false
    if (!citation || citation.citationId !== citationId || !isExpectedForm) return null

    nextCitationIndex += 1
    return citation
  }

  function transformChildren(parent: ParentNode) {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as Element
        if (element.tagName === 'A') {
          const citationId = element.getAttribute('href') ?? ''
          if (kAgentLegalCitationLinkHrefRegex.test(citationId)) {
            const citation = nextCitation(citationId, true)
            element.replaceWith(createLegalCitationNode(document, element, citation, viewerTargetsByCitationId[citationId]))
            continue
          }
        }

        transformChildren(element)
        continue
      }

      if (child.nodeType !== Node.TEXT_NODE) continue
      const textNode = child as Text
      const text = textNode.nodeValue ?? ''
      const matches = [...text.matchAll(kCitationMarkerRegex)]
      if (matches.length === 0) continue

      const fragment = document.createDocumentFragment()
      let lastIndex = 0

      for (const match of matches) {
        const index = match.index ?? 0
        if (index > lastIndex) fragment.append(document.createTextNode(text.slice(lastIndex, index)))

        const citationId = match[0].slice(1, -1)
        const citation = nextCitation(citationId, false)
        fragment.append(createReferenceMarkerNode(document, citation, viewerTargetsByCitationId[citationId]))
        lastIndex = index + match[0].length
      }

      if (lastIndex < text.length) fragment.append(document.createTextNode(text.slice(lastIndex)))
      textNode.replaceWith(fragment)
    }
  }

  transformChildren(template.content)
  return template.innerHTML
}

function createLegalCitationNode(
  ownerDocument: Document,
  originalLink: Element,
  citation: ReturnType<typeof labelAgentV2Citations>[number] | null,
  target: AgentV2ArtifactCitationViewerTarget | undefined
): Node {
  if (!citation?.isResolved) {
    return createDisabledCitationNode(ownerDocument, originalLink.childNodes, kAgentV2UnverifiedSourceLabel)
  }
  if (!target || !isCitationTargetUrl(target)) {
    return createDisabledCitationNode(ownerDocument, originalLink.childNodes, kAgentV2SourceUnavailableLabel)
  }

  const link = ownerDocument.createElement('a')
  link.setAttribute('href', target.viewerUrl)
  link.setAttribute('target', '_blank')
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('title', target.displayName)
  link.setAttribute(kArtifactReferenceMarkerIdAttr, citation.citationId)
  link.setAttribute('class', 'legal-citation-link text-brand-700 hover:text-brand-800')
  link.append(...Array.from(originalLink.childNodes))
  return link
}

function createReferenceMarkerNode(
  ownerDocument: Document,
  citation: ReturnType<typeof labelAgentV2Citations>[number] | null,
  target: AgentV2ArtifactCitationViewerTarget | undefined
): Node {
  const label = citation?.label ?? '?'
  if (!citation?.isResolved) {
    return createDisabledCitationNode(ownerDocument, [ownerDocument.createTextNode(`[${label}]`)], kAgentV2UnverifiedSourceLabel)
  }
  if (!target || !isCitationTargetUrl(target)) {
    return createDisabledCitationNode(ownerDocument, [ownerDocument.createTextNode(`[${label}]`)], kAgentV2SourceUnavailableLabel)
  }

  const link = ownerDocument.createElement('a')
  link.setAttribute('href', target.viewerUrl)
  link.setAttribute('target', '_blank')
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('title', target.displayName)
  link.setAttribute(kArtifactReferenceMarkerIdAttr, citation.citationId)
  link.setAttribute('aria-label', `Reference ${label}: ${target.displayName}`)
  link.setAttribute('class', 'reference-marker-badge text-brand-700 hover:text-brand-800')
  link.textContent = `[${label}]`
  return link
}

function createDisabledCitationNode(ownerDocument: Document, children: Iterable<Node>, title: string): HTMLSpanElement {
  const span = ownerDocument.createElement('span')
  span.setAttribute('class', 'text-brand-neutral-600 underline decoration-brand-neutral-600/40 underline-offset-2')
  span.setAttribute('title', title)
  span.setAttribute('aria-disabled', 'true')
  span.append(...Array.from(children))
  return span
}

function isCitationTargetUrl(target: AgentV2ArtifactCitationViewerTarget): boolean {
  if (target.kind === AgentV2ArtifactCitationTargetKind.WEB) {
    try {
      const url = new URL(target.viewerUrl)
      return (url.protocol === 'http:' || url.protocol === 'https:') && !url.username && !url.password
    } catch {
      return false
    }
  }
  return target.viewerUrl.startsWith('/dashboard/reference-viewer/') || /^\/dashboard\/m\/[^/]+\/reference-viewer\//.test(target.viewerUrl)
}
